import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Search, FileText, Download, BookOpen, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import MaterialDownloadForm from "@/components/MaterialDownloadForm";

const resources = [
    {
        id: 1,
        title: "Guia Completo de Automação Inteligente",
        description: "O manual definitivo para transformar sua operação com IA. Aprenda a identificar gargalos, escolher as ferramentas certas e medir o ROI da automação.",
        type: "Ebook",
        date: "2024-11-20",
        pages: "45 págs",
        category: "Ebooks",
        image: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=800&auto=format&fit=crop",
        featured: true,
        downloadLink: "#",
        relatedBlogPost: "/blog", // Link para o blog geral por enquanto ou um post específico se houver
    },
    {
        id: 2,
        title: "O Futuro do Service Desk: Tendências 2025",
        description: "Um whitepaper detalhado sobre como a IA Generativa está redefinindo as expectativas de atendimento e suporte corporativo.",
        type: "Whitepaper",
        date: "2024-11-15",
        pages: "28 págs",
        category: "Whitepapers",
        image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&auto=format&fit=crop",
        featured: false,
        downloadLink: "#",
        relatedBlogPost: "/blog",
    },
    {
        id: 3,
        title: "Checklist de Implementação de IA",
        description: "Um guia prático passo-a-passo para preparar sua empresa para a adoção de inteligência artificial em processos operacionais.",
        type: "Guia Prático",
        date: "2024-11-10",
        pages: "12 págs",
        category: "Guias Práticos",
        image: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&auto=format&fit=crop",
        featured: false,
        downloadLink: "#",
        relatedBlogPost: "/blog",
    },
    {
        id: 4,
        title: "ROI da Automação: Calculadora e Metodologia",
        description: "Aprenda a calcular o retorno sobre o investimento de projetos de automação e justificar o budget para a diretoria.",
        type: "Whitepaper",
        date: "2024-11-05",
        pages: "18 págs",
        category: "Whitepapers",
        image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop",
        featured: false,
        downloadLink: "#",
        relatedBlogPost: "/blog",
    },
    {
        id: 5,
        title: "Playbook de Gestão de Mudança",
        description: "Como engajar sua equipe e reduzir a resistência durante a implementação de novas tecnologias e processos.",
        type: "Guia Prático",
        date: "2024-10-28",
        pages: "32 págs",
        category: "Guias Práticos",
        image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop",
        featured: false,
        downloadLink: "#",
        relatedBlogPost: "/blog",
    },
    {
        id: 6,
        title: "Arquitetura de Dados para IA",
        description: "Entenda os requisitos técnicos e de infraestrutura para suportar soluções de IA robustas e escaláveis.",
        type: "Ebook",
        date: "2024-10-15",
        pages: "50 págs",
        category: "Ebooks",
        image: "https://images.unsplash.com/photo-1558494949-ef526b0042a0?w=800&auto=format&fit=crop",
        featured: false,
        downloadLink: "#",
        relatedBlogPost: "/blog",
    },
    {
        id: 7,
        title: "Módulo de Plano de Ação: 5W2H, GUT, A3 e 5 Porquês",
        description: "Guia completo do módulo de Plano de Ação da Aroan para transformar respostas críticas em planos estruturados, com priorização, análise de causa raiz, ações 5W2H e encerramento com lições aprendidas.",
        type: "Guia Prático",
        date: "2026-04-10",
        pages: "Guia técnico",
        category: "Guias Práticos",
        image: "/placeholder.svg",
        featured: false,
        downloadLink: "#",
        relatedBlogPost: "/blog",
    },
];

const categoryOrder = ["Ebooks", "Whitepapers", "Guias Práticos"];

const categories = [
    { name: "Todos", count: resources.length },
    ...categoryOrder.map((category) => ({
        name: category,
        count: resources.filter((resource) => resource.category === category).length,
    })),
];

const Resources = () => {
    const [selectedCategory, setSelectedCategory] = useState("Todos");
    const [searchQuery, setSearchQuery] = useState("");
    const [isDownloadFormOpen, setIsDownloadFormOpen] = useState(false);
    const [selectedResource, setSelectedResource] = useState<string>("");

    const handleDownloadClick = (resourceTitle: string) => {
        setSelectedResource(resourceTitle);
        setIsDownloadFormOpen(true);
    };

    const filteredResources = resources.filter(resource => {
        const matchesCategory = selectedCategory === "Todos" || resource.category === selectedCategory;
        const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            resource.description.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const featuredResource = resources.find(resource => resource.featured);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="Materiais Gratuitos sobre Service Desk, Automação e IA"
                description="E-books, whitepapers e guias práticos gratuitos sobre gestão de chamados, automação de service desk e uso de IA no atendimento."
                canonical="https://aroan.com.br/materiais"
                ogType="website"
                article={false}
            />
            <BreadcrumbSchema
                items={[
                    { name: "Home", url: "https://aroan.com.br/" },
                    { name: "Materiais", url: "https://aroan.com.br/materiais" },
                ]}
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-24 pb-16 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight animate-fade-in">
                            Materiais <span className="text-primary">Educativos</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto animate-fade-in-delay-1">
                            Ebooks, whitepapers e guias práticos para aprofundar seu conhecimento em automação inteligente e gestão de serviços.
                        </p>

                        {/* Search Bar */}
                        <div className="max-w-2xl mx-auto animate-fade-in-delay-2">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                                <Input
                                    type="text"
                                    placeholder="Buscar materiais..."
                                    className="pl-12 pr-4 py-6 text-lg"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Resource */}
            {featuredResource && (
                <section className="py-12 bg-background">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-8">
                            <Badge className="bg-primary text-primary-foreground">Destaque da Semana</Badge>
                        </div>
                        <div className="grid lg:grid-cols-2 gap-8 items-center bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-all duration-300 animate-scale-in">
                            <div className="relative h-64 lg:h-full min-h-[400px]">
                                <img
                                    src={featuredResource.image}
                                    alt={featuredResource.title}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-8 lg:p-12">
                                <div className="flex items-center gap-3 mb-4">
                                    <Badge variant="outline">{featuredResource.category}</Badge>
                                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                                        <BookOpen className="w-4 h-4" /> {featuredResource.pages}
                                    </span>
                                </div>
                                <h2 className="text-3xl sm:text-4xl font-bold mb-4 hover:text-primary transition-colors">
                                    {featuredResource.title}
                                </h2>
                                <p className="text-muted-foreground mb-6 leading-relaxed text-lg">
                                    {featuredResource.description}
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Button className="group" size="lg" onClick={() => handleDownloadClick(featuredResource.title)}>
                                        <Download className="mr-2 w-4 h-4" />
                                        Baixar Agora
                                    </Button>
                                    <Button variant="outline" className="group" asChild>
                                        <a href={featuredResource.relatedBlogPost}>
                                            Ler Artigo Relacionado
                                            <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Categories Filter */}
            <section className="py-8 bg-background border-y border-border">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap gap-3 justify-center">
                        {categories.map((category, index) => (
                            <Button
                                key={index}
                                variant={selectedCategory === category.name ? "default" : "outline"}
                                onClick={() => setSelectedCategory(category.name)}
                                className="transition-all duration-300"
                            >
                                {category.name}
                                <Badge variant="secondary" className="ml-2">{category.count}</Badge>
                            </Button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Resources Grid */}
            <section className="py-20 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {filteredResources.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-xl text-muted-foreground">
                                Nenhum material encontrado. Tente uma busca diferente.
                            </p>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredResources.map((resource, index) => (
                                <article
                                    key={resource.id}
                                    className="group bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-all duration-300 animate-scale-in flex flex-col"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={resource.image}
                                            alt={resource.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute top-4 left-4">
                                            <Badge variant="secondary">{resource.category}</Badge>
                                        </div>
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                                            <FileText className="w-3 h-3" />
                                            <span>{resource.type}</span>
                                            <span>•</span>
                                            <span>{resource.pages}</span>
                                        </div>
                                        <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                                            {resource.title}
                                        </h3>
                                        <p className="text-muted-foreground mb-6 leading-relaxed line-clamp-3 text-sm flex-1">
                                            {resource.description}
                                        </p>

                                        <div className="flex items-center gap-3 mt-auto pt-4 border-t border-border/50">
                                            <Button className="flex-1 group/btn" variant="default" size="sm" onClick={() => handleDownloadClick(resource.title)}>
                                                <Download className="mr-2 w-4 h-4" />
                                                Baixar
                                            </Button>
                                            <Button variant="ghost" size="icon" asChild title="Ler Artigo Relacionado">
                                                <a href={resource.relatedBlogPost}>
                                                    <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-primary transition-colors" />
                                                </a>
                                            </Button>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <Footer />

            <MaterialDownloadForm
                open={isDownloadFormOpen}
                onOpenChange={setIsDownloadFormOpen}
                resourceTitle={selectedResource}
            />
        </div>
    );
};

export default Resources;
